/**
 * Data pages in the editor (2026-10-16): stand-alone pages drawn from our data — the media plan, the brand's live Meta
 * ads, a competitor's ads, TikTok trends, campaign results… — added like a template page.
 *
 * - "Add page" lists them under Live data; one picks the settings (a plan, a competitor, a market) and the page lands
 *   after the current one, in this deck's look.
 * - A data page is a snapshot: its badge says "as of <date>"; Refresh rebuilds it with the same settings and asks first
 *   when someone changed the page by hand.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BarChart3, ClipboardList, Globe, Image as ImageIcon, Loader2, Megaphone, RefreshCw, Search, Sparkles, Swords, TrendingUp, UserSearch, Users, X, Zap, type LucideIcon } from 'lucide-react';
import { coworkApi, type DataPageType } from '@/cowork/omniad/api/coworkApi';
import { useDataPages, withSig } from './dataPageUtils';

const ICON: Record<string, LucideIcon> = { 'media-plan': ClipboardList, 'client-meta-ads': Megaphone, 'competitor-ads': Swords, 'tiktok-trends': TrendingUp, 'breakout-formats': Sparkles, 'campaign-results': BarChart3, 'top-posts': ImageIcon, 'best-hooks': Zap, audiences: Users, 'market-brief': Globe, 'rex-creators': UserSearch, 'web-research': Search };
const GROUPS = ['Planning', 'Your results', 'Market & competitors', 'Creators & content'];

type Slide = Record<string, unknown>;

/** The Live data tiles in "Add page". */
export function DataPagesSection({ onPick }: { onPick: (t: DataPageType) => void }) {
  const q = useDataPages();
  const types = q.data?.types || [];
  if (!types.length) return null;
  return (
    <div className="mb-3">
      <div className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#667085]">Live data · a page drawn from your data, as of today</div>
      <div className="grid grid-cols-3 gap-2">
        {[...types].sort((a, b) => GROUPS.indexOf(a.group) - GROUPS.indexOf(b.group)).map((t) => {
          const Icon = ICON[t.id] || BarChart3;
          return (
            <button key={t.id} onClick={() => onPick(t)} className="flex items-start gap-2.5 rounded-lg border border-[#eaecf0] px-3 py-2.5 text-left hover:border-[#be43ff] hover:bg-[#fcf7ff]" title={t.description}>
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#be43ff]" />
              <span className="min-w-0"><span className="block text-[12.5px] font-semibold text-[#101828]">{t.name}{t.staffOnly && <span className="ml-1 text-[10px] font-medium text-[#98a2b3]">staff</span>}</span><span className="line-clamp-2 block text-[11px] text-[#667085]">{t.description}</span></span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Settings for one data page, then build it. */
export function DataPageDialog({ type, brandId, deckId, initial, onAdd, onClose }: { type: DataPageType; brandId?: string; deckId: string; initial?: Record<string, string>; onAdd: (slide: Slide) => void; onClose: () => void }) {
  const markets = useDataPages().data?.markets || [];
  const [values, setValues] = useState<Record<string, string>>(() => ({ ...Object.fromEntries(type.params.filter((p) => p.kind === 'market').map((p) => [p.key, 'SE'])), ...(initial || {}) }));
  const [options, setOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!type.params.some((p) => p.kind === 'select')) return;
    let off = false;
    coworkApi.presentations.dataPageOptions(type.id, brandId).then((r) => { if (off) return; setOptions(r.options || {}); setValues((v) => { const n = { ...v }; for (const p of type.params) if (p.kind === 'select' && !n[p.key] && r.options?.[p.key]?.[0]) n[p.key] = r.options[p.key][0].value; return n; }); }).catch(() => undefined);
    return () => { off = true; };
  }, [type.id, brandId]);
  const missing = type.params.filter((p) => p.required && !String(values[p.key] || '').trim());
  const build = async () => {
    if (missing.length || busy) return;
    setBusy(true); setError('');
    try { const r = await coworkApi.presentations.buildDataPage(type.id, { brandId, deckId, params: values }); onAdd(withSig(r.slide)); }
    catch (e) { setError((e as Error).message || 'Could not build that page.'); }
    finally { setBusy(false); }
  };
  const Icon = ICON[type.id] || BarChart3;
  const field = 'mt-1 w-full rounded-lg border border-[#d0d5dd] px-2.5 py-1.5 text-[13px] text-[#101828] focus:border-[#be43ff] focus:outline-none';
  return createPortal(
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/30" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div role="dialog" aria-label={type.name} className="w-[420px] rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#be43ff]" />
          <div className="min-w-0 flex-1"><div className="text-[15px] font-semibold text-[#101828]">{type.name}</div><div className="mt-0.5 text-[12.5px] text-[#667085]">{type.description}</div></div>
          <button onClick={onClose} disabled={busy} className="rounded p-1 text-[#98a2b3] hover:bg-[#f2f4f7]" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-4 space-y-3">
          {type.params.map((p) => (
            <label key={p.key} className="block text-[12px] font-medium text-[#344054]">{p.label}
              {p.kind === 'text'
                ? <input value={values[p.key] || ''} onChange={(e) => setValues((v) => ({ ...v, [p.key]: e.target.value }))} placeholder={p.help} className={field} onKeyDown={(e) => { if (e.key === 'Enter') void build(); }} />
                : <select value={values[p.key] || ''} onChange={(e) => setValues((v) => ({ ...v, [p.key]: e.target.value }))} className={field}>
                    {(p.kind === 'market' ? markets : options[p.key] || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    {p.kind === 'select' && !(options[p.key] || []).length && <option value="">Nothing to pick for this brand yet</option>}
                  </select>}
            </label>
          ))}
          {!type.params.length && <div className="text-[12.5px] text-[#667085]">No settings: it uses this deck's brand.</div>}
        </div>
        {error && <div className="mt-3 rounded-lg bg-[#fef3f2] px-3 py-2 text-[12.5px] text-[#b42318]">{error}</div>}
        <div className="mt-5 flex items-center justify-end gap-2">
          {busy && <span className="mr-auto flex items-center gap-1.5 text-[12px] text-[#667085]"><Loader2 className="h-3.5 w-3.5 animate-spin text-[#be43ff]" />Fetching the data{/ads/.test(type.id) ? ' (up to 40 s)' : ''}</span>}
          <button onClick={onClose} disabled={busy} className="rounded-lg border border-[#d0d5dd] px-3 py-1.5 text-[13px] font-medium text-[#344054] hover:bg-[#f9fafb] disabled:opacity-50">Cancel</button>
          <button onClick={() => void build()} disabled={busy || missing.length > 0} className="rounded-lg bg-[#be43ff] px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-[#a52fe6] disabled:opacity-50">{initial ? 'Refresh page' : 'Add page'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Above a data page: what it is, as of when, and Refresh. */
export function DataPageBadge({ slide, onRefresh, busy }: { slide: Slide; onRefresh: () => void; busy: boolean }) {
  const d = slide._data as { type?: string; asOf?: string; source?: string } | undefined;
  if (!d?.type) return null;
  const when = d.asOf ? new Date(d.asOf).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  return (
    <div className="mb-2 flex items-center gap-2 rounded-lg border border-[#e9d7fe] bg-[#faf5ff] px-3 py-1.5 text-[12px] text-[#6941c6]">
      <span className="shrink-0 font-semibold">Live data{when ? ` · as of ${when}` : ''}</span>
      <span className="min-w-0 flex-1 truncate text-[#7f56d9]" title={d.source}>{d.source}</span>
      <button onClick={onRefresh} disabled={busy} className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-medium hover:bg-[#f4ebff] disabled:opacity-50" title="Build this page again with today's data"><RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} />Refresh</button>
    </div>
  );
}
